<template>
  <div class="large-12">
    <div class="row wrapper-spacing">
      <div class="medium-12 large-12 columns">
        <h3>Stichtag</h3>
        <div v-if="!showStyle" class="spv-select-group flex-no-wrap row">
          <div
            v-for="d in deadlines"
            :key="d.id"
            :class="`small-6 medium-4 large-4 columns`"
          >
            <div class="radio-input">
              <input
                :id="`${ indexId }-${ d.id }`"
                :checked="deadline_id == d.id"
                :value="d.id"
                :name="`${ indexId }-deadline`"
                type="radio"
                @change="onSelectDeadline(d.id)"
              >
              <label :class="{ 'checked': deadline_id == d.id }" :for="`${ indexId }-${ d.id }`">
                <p>{{ d.name }}</p>
              </label>
            </div>
          </div>
        </div>

        <div v-else class="spv-select-group">
          <div class="small-12 medium-6 large-4">
            <select :value="deadline_id" class="spv-input-select" @change="(e) => onSelectDeadline(e.target.value)">
              <option v-for="d in deadlines" :key="d.id" :value="d.id">
                {{ d.name }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <div class="row wrapper-spacing">
      <div class="medium-12 large-6 columns">
        <h3>Zeitraum</h3>
        <div class="large-8">
          <select :value="year_" class="spv-input-select" @change="(e) => onSelectYear(e.target.value)">
            <option v-for="y in years" :value="y" :key="y">
              {{
                deadline_id === 'j'
                  ? `Jun ${ y } - Mai ${ y + 1 }`
                  : `Dez ${ y } - Nov ${ y + 1 }`
              }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <div class="row wrapper-spacing">
      <div class="medium-12 large-12 columns">
        <h1>Normierte Wertentwicklung <span v-html="indexName_"/> {{ deadline_.name }}</h1>
        <div class="large-12 columns spv-chart-wrapper">
          <index-chart
            v-show="deadline_id === 'j'"
            :data="chart_items['j'] || []"
            :max="chart_max['j'] || 0"
            :name="indexName_"
            color="#E81700"
          />

          <index-chart
            v-show="deadline_id === 'd'"
            :data="chart_items['d'] || []"
            :max="chart_max['d'] || 0"
            :name="indexName_"
            color="#E81700"
          />

          <div class="spv-price-inquiry">
            <b class="spv-price-inquiry__label">Kursabfrage</b>

            <div class="spv-price-inquiry__date-picker">
              <span>Datum</span>
              <date-picker
                :min="minDate"
                :max="maxDate"
                :value="inquiry_date"
                @select="onSelectInquiryDate"
              />
            </div>

            <div class="spv-price-inquiry__value">
              <span v-if="!inquiry_valid && !inquiry_date" class="error-value">
                Bitte wählen Sie ein Datum
              </span>
              <span v-else-if="inquiry_value === -1" class="error-value">
                Für dieses Datum liegt kein Datensatz vor. Bitte wählen Sie ein anderes Datum aus
              </span>
              <span v-else-if="!!inquiry_value">
                <span v-html="indexName_"/>&nbsp;&nbsp;<b>{{ inquiry_value }} Punkte</b>
              </span>
            </div>

            <a class="button button-small block" @click="loadDayValue">Anzeigen</a>
          </div>
        </div>
      </div>
    </div>

    <div v-if="configText" class="row wrapper-spacing">
      <div class="medium-12 large-12 columns">
        <div v-html="configText" />
      </div>
    </div>

    <template v-if="show_data">
      <index-euro-table
        v-if="show_percent"
        :index-id="indexId"
        :index-name="indexName_"
        :items="details_data.index_values || []"
        :info-texts="gloss_texts_vip_euro"
        :month="deadline_.name"
        :target-vol="targetVol"
        :yearly-yield="details_data.yearly_yield"
        :decisive-yield="details_data.decisive_yearly_yield"
        @print="onPrint"
      />

      <index-trend-table
        v-else
        :index-id="indexId"
        :index-name="indexName_"
        :items="details_data.index_values || []"
        :month="deadline_.name"
        :year="details_data.year"
        :target-vol="targetVol"
        :yearly-yield="details_data.yearly_yield"
        :decisive-yield="details_data.decisive_yearly_yield"
        :factsheet-path="factsheet_path[deadline_id]"
        @print="onPrint"
        @download-pdf="onDownloadFactsheet"
        @open-pdf="onOpenFactsheet"
      />
    </template>

    <div v-if="lastConstituent && indexId === 'trend'" class="row wrapper-spacing">
      <div class="medium-12 large-12 columns">
        <h1>Zusammensetzung der Anlageklassen TrendPortfolio Index {{ deadline_.name }}</h1>
        <p><b>Stand {{ `${ lastConstituent.month_name } ${ lastConstituent.year }` }}</b></p>
        <p>Die für den angegebenen Monat ausgewählten Anlageklassen sind in rot dargestellt.</p>
        <p>Die ausgewählten Anlageklassen gehen gleichgewichtet (zu je 25 %) in den Index ein. Falls weniger als vier Anlageklassen positive Trends aufzeigen und damit nicht in diesem Monat in den TrendPortfolio Index fließen, wird entsprechend weniger investiert.</p>

        <div class="spv-asset-classes">
          <template v-for="(item, index) in constituent_data[deadline_id]">
            <div :key="index" class="small-12 medium-6 large-4 spv-card-widget">
              <div :class="[ 'spv-card-widget__card', { 'marked': item.weight === 25 }]">
                <h2>{{ item.name }}</h2>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <div v-if="indexId === 'deka'" class="row wrapper-spacing">
      <div class="medium-12 large-12 columns">
        <h1>Zusammensetzung der Anlageklassen {{ indexName_ }} {{ deadline_.name }}</h1>
        <p><b>Stand {{ `${ getAssetsMonthName(deka_assets.assets_month) } ${ deka_assets.assets_year }` }}</b></p>
        <p v-html="configTextOfAsset" />
      </div>

      <div class="medium-12 large-12 columns">
        <n-accordion-chart :items="dekaAssetsChartData" />
      </div>
    </div>

    <div v-if="indexId === 'climate'" class="row wrapper-spacing">
      <div class="medium-12 large-12 columns">
        <h1>Zusammensetzung der Aktien des {{ indexName_ }} {{ deadline_.name }}</h1>
        <p><b>Stand {{ `${ getAssetsMonthName(climate_assets.assets_month) } ${ climate_assets.assets_year }` }}</b></p>
        <p v-html="configTextOfAsset" />
      </div>

      <div class="small-12 medium-12 large-12 columns chart-spacing">
        <n-accordion-chart :items="climateAssetsChartData" />
      </div>
    </div>

    <div v-if="configText60" class="row wrapper-spacing">
      <div class="medium-12 large-12 columns">
        <br>
        <h2>Details zum Indexportfolio</h2>
        <div v-html="configText60" />
      </div>
    </div>

    <div v-if="climate_assets.assets" class="row wrapper-spacing">
      <div class="small-12 medium-12 large-12 columns">
        <div class="spv-table-wrapper">
          <input
            :class="`climate-asset-filter`"
            :type="`text`"
            :placeholder="`Suche nach Name, Land oder Industrie`"
            :value="asset_filter"
            @input="(e) => asset_filter = e.target.value"
          >

          <table class="spv-product-table spv-index-table spv-index-table--climate">
            <thead>
              <tr>
                <th>Name</th>
                <th>Land</th>
                <th>Industrie</th>
                <th>Gewichtung</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="(asset, index) in climateAssets" :key="index">
                <td>{{ asset.name }}</td>
                <td>{{ asset.country }}</td>
                <td>{{ asset.industry }}</td>
                <td>{{ formatPercent(asset.weight, 2) }}</td>
              </tr>
            </tbody>
          </table>

          <div v-if="climate_assets.assets.length > 10" class="text-center">
            <a v-if="!asset_filter" class="toggle-expand" @click="show_all_rows = !show_all_rows">
              <i v-if="!show_all_rows" class="fo fo-arrow-down" />
              <i v-else class="fo fo-arrow-up" />
            </a>
          </div>
        </div>
      </div>
    </div>

    <div v-if="factsheet_path[deadline_id] && indexId !== 'trend'" class="row wrapper-spacing">
      <div class="medium-12 large-12 columns">
        <div>
          <a class="button call-to-action" @click.stop="onOpenFactsheet(factsheet_path[deadline_id])">
            Download Factsheet
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import mixin from './mixin';

import IndexTrendTable from './index-trend-table';
import IndexEuroTable from './index-euro-table';
import { b64DecodeUnicode } from '@/modules/utils';

export default {
  name: 'index_general',

  components: {
    IndexTrendTable,
    IndexEuroTable
  },

  mixins: [
    mixin
  ],

  data: () => ({
    deadline_id: 'j',

    deadlines: [
      {
        id: 'j',
        name: 'Juni',
        label: '01.06.'
      },
      {
        id: 'd',
        name: 'Dezember',
        label: '01.12.',
      }
    ],

    config_values: null,
    index_data: null,
    index_name: '',
    show_data: false,
    show_percent: false,

    details_data: [],

    chart_items: {},
    chart_max: {},

    constituent_data: {},
    factsheet_path: {},

    deka_assets: {},
    climate_assets: {},

    asset_filter: '',
    show_all_rows: false,

    climate_info_texts: {
      'Energie': 'Entdecker, Raffinerien, Vermarkter und Vertreiber von fossilen Brennstoffen, Uran und erneuerbaren Energien, Hersteller energiebezogener Geräte und Anbieter unterstützender Dienstleistungen',
      'Finanzen': 'Betreiber von Privat-, Geschäfts- und Investmentbanken, Investment Trusts und Finanzmärkten sowie Anbieter von Investment- und Versicherungsdienstleistungen.',
      'Gesundheitswesen': 'Hersteller, Entwickler und Vermarkter von Pharmazeutika, medizinischen Geräten und Verbrauchsmaterialien sowie fortschrittlichen therapeutischen Behandlungen und Geräten sowie Anbieter von Gesundheitseinrichtungen und medizinischer Forschung und Entwicklung.',
      'Grundeigentum': 'Unternehmen, die sich mit der Entwicklung und / oder Verwaltung von Immobiliengeschäften und damit verbundenen Dienstleistungen befassen, gewerbliche, diversifizierte und spezialisierte (Gesundheitswesen, Gastgewerbe, Selbstlagerung, Holz, Hypothek und islamische) Immobilieninvestmentfonds.',
      'Grundmaterialien': 'Hersteller, Extraktoren und Raffinerien von Chemikalien, Mineralien, Edelmetallen, Stahl, Aluminium, Forstprodukten sowie Bau- und anderen Rohstoffen.',
      'Industriell': 'Hersteller von Industrieanlagen und Handelsbedarf, Anbieter verwandter Dienstleistungen wie diversifizierter Handel, Vertriebs- und Transportdienstleistungen sowie Anbieter professioneller Informationsdienste.',
      'Nichtzyklische Verbraucher': 'Hersteller, Verarbeiter und Einzelhändler von Lebensmitteln, Tabak und Getränken sowie Hersteller von Haushalts- und persönlichen Produkten sowie Anbieter persönlicher Dienstleistungen.',
      'Technologie': 'Hersteller von Halbleitern, Kommunikationsgeräten, Computerhardware und technologiebezogenen Bürogeräten, Anbieter von Beratung, IT-Dienstleistungen und Finanztechnologie (Fintech) sowie Infrastruktur. Eigentümer und Betreiber von Festnetz- und drahtlosen Telekommunikationsnetzen, die Sprach- und Datendienste anbieten.',
      'Verbraucherzyklika': 'Hersteller von Automobilen, Haushaltswaren, Textilien und anderen Produkten sowie Bauherren und Einzelhändler und Anbieter von Verbraucherdienstleistungen wie Hotel-, Unterhaltungs- und Mediendienstleistungen.',
      'Institutionen, Verbände & Organisationen': 'Institutionen, Verbände und Organisationen, die sich mit der Verwaltung und Förderung der Interessen und Werte ihrer Mitglieder befassen.',
      'Regierungsaktivität': 'Organisationen, die sich mit regierungsbezogenen Aktivitäten befassen. Dazu gehören öffentliche Finanzen, Polizei, Feuerwehr, Rechtswesen, Verwaltung, nationale Sicherheit und internationale Angelegenheiten.',
      'Akademische und pädagogische Dienstleistungen': 'Organisationen, die sich mit der Bereitstellung, Verwaltung und Leitung akademischer Kurse, nicht-akademischer Ausbildung und Schulung befassen.',
      'Dienstprogramme': 'Produzenten und Vertreiber von Strom, Erdgas, Wasser und Dampf, unter anderem Versorgungsunternehmen.',
    }
  }),

  computed: {
    configText() {
      if (!this.config_values) {
        return '';
      }

      let key = '';

      if (this.indexId === 'trend') {
        key = this.deadline_id === 'd'
          ? 'trend_desc_graph_dez'
          : 'trend_desc_graph_juni';
      } else {
        key = this.deadline_id === 'd'
          ? `${ this.indexId }_einleitung_dezember`
          : `${ this.indexId }_einleitung_juni`;
      }

      return this.config_values[key]
        ? b64DecodeUnicode(this.config_values[key].value)
        : '';
    },

    configTextOfAsset() {
      const textKey = {
        deka: {
          j: 'deka_asset_juni',
          d: 'deka_asset_dezember',
        },

        climate: {
          j: 'climate_asset_juni',
          d: 'climate_asset_dezember',
        }
      };

      if (!this.config_values || !textKey[this.indexId]) {
        return '';
      }

      const k = textKey[this.indexId][this.deadline_id];
      return b64DecodeUnicode(this.config_values[k].value);
    },

    configText60() {
      const textKey = {
        climate: {
          j: 'climate_beschreibung_60_aktien_juni',
          d: 'climate_beschreibung_60_aktien_dezember',
        }
      };

      if (!this.config_values || !textKey[this.indexId]) {
        return '';
      }

      const k = textKey[this.indexId][this.deadline_id];
      return b64DecodeUnicode(this.config_values[k].value);
    },

    deadline_ () {
      return this.deadlines.find(d => d.id === this.deadline_id);
    },

    dekaAssetsChartData () {
      if (!this.deka_assets.assets) {
        return [];
      }

      const assets = this.deka_assets.assets;
      const children2 = (a, a_) => assets
        .filter(a__ => a_.l1 === a.l1 && a__.l2 === a_.l2 && a__.l3)
        .sort((a1, a2) => a1.percent < a2.percent ? 1 : -1)
        .map(a__ => ({
          label: a__.l3,
          percent: a__.percent,
        }));

      const children1 = (a) => assets
        .filter(a_ => a_.l1 === a.l1 && a_.l2 && !a_.l3)
        .sort((a1, a2) => a1.percent < a2.percent ? 1 : -1)
        .map(a_ => ({
          label: a_.l2,
          percent: a_.percent,
          children: children2(a, a_)
        }));

      return assets
        .filter(a => !a.l2 && !a.l3)
        .sort((a1, a2) => a1.percent < a2.percent ? 1 : -1)
        .map(a => ({
          label: a.l1,
          percent: a.percent,
          children: children1(a)
        }));
    },

    climateAssets () {
      const assets = this.climate_assets.assets;
      if (!assets) {
        return [];
      }

      if (!this.asset_filter) {
        return this.show_all_rows
          ? assets
          : assets.slice(0, 10);
      }

      const search_str = this.asset_filter.toLowerCase();

      return assets.filter(a => (
        a.name.toLowerCase().search(search_str) >= 0 ||
        a.country.toLowerCase().search(search_str) >= 0 ||
        a.industry.toLowerCase().search(search_str) >= 0
      ));
    },

    climateAssetsChartData () {
      if (!this.climate_assets.assets) {
        return [];
      }

      const assets = this.climate_assets.assets;
      const items = [];
      let sum_weight = 0;

      assets.forEach(a => {
        const id = items.findIndex(i => i.label === a.industry);
        const weight = parseFloat(a.weight);
        sum_weight += weight;

        if (id >= 0) {
          items[id].value += weight;
          return;
        }

        items.push({
          label: a.industry,
          percent: 0,
          value: weight
        });
      });

      const children = (item) => assets
        .filter(a => a.industry === item.label)
        .sort((a, b) => a.weight < b.weight ? 1 : -1)
        .map(a => ({
          label: a.name,
          percent: (parseFloat(a.weight) / item.value) * 100,
          // value: a.weight
        }));

      const chartItems = items
        .sort((a, b) => a.value < b.value ? 1 : -1)
        .map(item => ({
          label: item.label,
          percent: (item.value / sum_weight) * 100,
          info_text: this.climate_info_texts[item.label],
          children: children(item)
        }));

      return chartItems;
    },

    indexName_ () {
      return this.index_name;
    },

    years () {
      if (!this.index_data) {
        return [];
      }

      return (this.index_data[this.deadline_id].years || [])
        .sort((a, b) => b - a);
    },

    maxDate () {
      if (!this.index_data) {
        return moment().format('YYYY-MM-DD');
      }

      return this.index_data[this.deadline_id].min_max.max_date;
    },

    minDate () {
      if (!this.index_data) {
        return moment().format('YYYY-MM-DD');
      }

      return this.index_data[this.deadline_id].min_max.min_date;
    },

    targetVol() {
      return this.details_data && this.details_data.target_vol
        ? this.formatPercent(this.details_data.target_vol * 100, 2)
        : '0,00%';
    },

    lastConstituent() {
      if (!this.constituent_data[this.deadline_id]) {
        return null;
      }

      const length = this.constituent_data[this.deadline_id].length;

      if (!length) {
        return null;
      }

      return this.constituent_data[this.deadline_id][length - 1];
    },
  },

  methods: {
    getAssetsMonthName (assets_month) {
      if (!assets_month) {
        return '';
      }

      return this.month_names[assets_month - 1];
    },

    loadIndexConfig () {
      if (!this.apiRequest || this.years.length > 0) {
        return;
      }

      const params = {
        index_name: this.indexId
      };

      this.apiRequest
        .get(this.config.paths.years, {
          params
        })
        .then(res => {
          this.config_values = res.data.config_values;
          this.index_name = res.data.display_index_name;
          this.index_data = res.data.index_data;
          this.show_data = res.data.show_data;
          this.show_percent = res.data.show_percent;

          this.year_ = this.years[0];
          this.inquiry_date = this.maxDate;

          this.deadlines.forEach(d => {
            this.loadDailyData(d.id);
          });

          this.loadDetails();

          if (this.indexId === 'trend') {
            this.loadTrendConfig();
          } else if (this.indexId === 'deka') {
            this.loadDekaConfig();
          } else if (this.indexId === 'climate') {
            this.loadClimateConfig();
          }
        });
    },

    loadTrendConfig () {
      this.apiRequest
        .get(this.config.paths.trend_config)
        .then(res => {
          this.constituent_data = {
            j: res.data.constituent_data_june,
            d: res.data.constituent_data_dec,
          };

          this.factsheet_path = {
            j: res.data.fact_sheet_june,
            d: res.data.fact_sheet_dec
          };
        });
    },

    loadDekaConfig () {
      this.apiRequest
        .get(this.config.paths.deka_config)
        .then(res => {
          this.deka_assets = {
            assets: res.data.assets,
            assets_month: res.data.assets_month,
            assets_year: res.data.assets_year,
          };

          this.factsheet_path = {
            j: res.data.factsheet_path_jun,
            d: res.data.factsheet_path_dec
          };
        });
    },

    loadClimateConfig () {
      this.apiRequest
        .get(this.config.paths.climate_config)
        .then(res => {
          this.climate_assets = {
            assets: res.data.assets,
            assets_month: res.data.assets_month,
            assets_year: res.data.assets_year,
          };

          this.factsheet_path = {
            j: res.data.factsheet_path_jun,
            d: res.data.factsheet_path_dec
          };
        });
    },

    loadDetails () {
      if (!this.show_data) {
        return;
      }

      let year = parseInt(this.year_);

      if (!year || isNaN(year)) {
        return;
      }

      const params = {
        index_name: this.indexId,
        type: this.deadline_id,
        effektive_year: year,
      };

      this.apiRequest
        .get(this.config.paths.query, {
          params
        })
        .then(res => {
          this.details_data = res.data || {};

          if (this.details_data.target_vol) {
            this.details_data.target_vol = this.details_data.target_vol !== '<nil>'
              ? parseFloat(this.details_data.target_vol)
              : 0;
          }
        })
        .catch(() => {
          this.details_data = {};
        });
    },

    loadDailyData (type) {
      if (!this.apiRequest || !!this.chart_items[this.deadline_id]) {
        return;
      }

      const params = {
        index_name: this.indexId,
        type
      };

      this.apiRequest
        .get(this.config.paths.daily, {
          params
        })
        .then(res => {
          this.setChartData(type, res.data || []);
        });
    },

    loadDayValue() {
      if (!this.inquiry_date) {
        this.inquiry_value = '';
        this.inquiry_valid = false;
        return;
      }

      this.inquiry_valid = true;

      let params = {
        index_name: this.indexId,
        type: this.deadline_id,
        date: this.formatDate(this.inquiry_date, 'YYYY-MM-DD[T]HH:mm:ss.SSS[Z]')
      };

      this.apiRequest
        .get(this.config.paths.day, {
          params
        })
        .then(res => {
          this.inquiry_value = res.data.index_value
            ? this.formatMoney(res.data.index_value, 2)
            : -1;
        })
        .catch(err => {
          console.error(err);
          this.inquiry_value = -1;
        });
    },

    onPrint() {
      let year_ = parseInt(this.year_);

      if (!year_ || isNaN(year_)) {
        return;
      }

      const params = {
        index_name: this.indexId,
        year: year_,
        data_type: this.deadline_id
      };

      params.anticache = moment().format('YYYYMMDDHHmmss');

      this.apiRequest.get(this.config.paths.pdf, {
        params,
        responseType: 'blob'
      })
        .then(res => {
          this.downloadBlob(res.data);
        });
    },

    onDownloadFactsheet (id) {
      const params = {
        id
      };

      this.apiRequest
        .get(this.config.paths.factsheet_download, {
          params,
          responseType: 'blob'
        })
        .then(res => {
          this.downloadBlob(res.data, this.getFileNameFromHeader(res.headers, 'trend_fact_sheet.pdf'));
        });
    },

    onSelectDeadline (value) {
      this.deadline_id = value;
      this.inquiry_value = '';
      this.inquiry_valid = false;
      this.inquiry_date = this.maxDate;

      this.loadDetails();
    },

    setChartData (type, data) {
      this.chart_max[type] = 0;

      this.chart_items[type] = data
        .sort((a, b) => a.date > b.date ? 1 : -1)
        .map(item => {
          let value = parseFloat(item.index_value);

          if (value > this.chart_max[type]) {
            this.chart_max[type] = value;
          }

          return [
            moment(item.date).valueOf(),
            value
          ];
        });

      this.chart_items = {
        ...this.chart_items
      };
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/apps/index/spv_general.vue